import React, { useImperativeHandle, useState } from 'react';
import { styled } from '@mui/material/styles';
import { IconButton } from '@mui/material';
import { Forward5Outlined, PauseCircleOutline, Replay5Outlined } from '@mui/icons-material';
import { BrandAwarenessIcon } from '../Icons';

interface Props {
  className?: string;
  audioSrc: string;
  onStart?: () => void;
  onEnded?: () => void;
  onError?: () => void;
}

export type AudioPlayerProps = Props;

export interface AudioPlayerApi {
  next: (src: string, autoplay: boolean) => void;
}

const PlayerContainer = styled('div')(({ theme }) => ({
  borderBottom: `0.5px solid ${theme.palette.divider}`,
}));

const PlayerBar = styled('div')(({ theme }) => ({
  padding: theme.spacing(0.5, 2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  maxWidth: 600,
  marginLeft: 'auto',
  marginRight: 'auto',
}));

const REWIND_SECONDS = 5;

const formatTime = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.floor(timeInSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const AudioPlayer = React.forwardRef<AudioPlayerApi, Props>(
  ({ audioSrc, className, onStart, onEnded, onError }, ref) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioRef = React.useRef<HTMLAudioElement>(null);

    useImperativeHandle(ref, () => ({
      next: (src: string, autoplay: boolean) => {
        if (!audioRef.current) return;
        audioRef.current.src = src;
        if (autoplay) {
          audioRef.current.play();
          setIsPlaying(true);
        }
      },
    }));

    React.useEffect(() => {
      if (!audioRef.current) return;

      const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        setCurrentTime(audioRef.current.currentTime);
      };
      const audio = audioRef.current;
      audio.addEventListener('timeupdate', handleTimeUpdate);

      return () => {
        audio.removeEventListener('timeupdate', handleTimeUpdate);
      };
    }, []);

    React.useEffect(() => {
      if (!audioRef.current) return;

      const handleDurationChange = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
      };
      const audio = audioRef.current;
      audio.addEventListener('durationchange', handleDurationChange);

      return () => {
        audio.removeEventListener('durationchange', handleDurationChange);
      };
    }, []);

    // reset player when replay has ended
    React.useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        onEnded?.();
      };

      audio.addEventListener('ended', handleEnded);

      return () => audio.removeEventListener('ended', handleEnded);
    }, [onEnded]);

    // reset player on error
    React.useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handleError = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        onError?.();
      };

      audio.addEventListener('error', handleError);

      return () => audio.removeEventListener('error', handleError);
    }, [onError]);

    const handlePlayPause = () => {
      if (!audioRef.current) return;
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // do not notify callback on resume
        if (currentTime === 0) {
          onStart?.();
        }

        audioRef.current.play();
      }
      setIsPlaying(prev => !prev);
    };

    const handleRewind = () => {
      if (!audioRef.current) return;
      audioRef.current.currentTime -= REWIND_SECONDS;
    };

    const handleFastForward = () => {
      if (!audioRef.current) return;
      audioRef.current.currentTime += REWIND_SECONDS;
    };

    return (
      <PlayerContainer className={className}>
        <PlayerBar>
          <div>{formatTime(currentTime)}</div>
          <IconButton color={'primary'} size={'large'} onClick={handleRewind}>
            <Replay5Outlined fontSize="inherit" />
          </IconButton>
          <IconButton color={'primary'} size={'large'} onClick={handlePlayPause}>
            {isPlaying ? <PauseCircleOutline fontSize="inherit" /> : <BrandAwarenessIcon fontSize="inherit" />}
          </IconButton>
          <IconButton color={'primary'} size={'large'} onClick={handleFastForward}>
            <Forward5Outlined fontSize="inherit" />
          </IconButton>
          <div>{formatTime(duration)}</div>
          <audio ref={audioRef} src={audioSrc} />
        </PlayerBar>
      </PlayerContainer>
    );
  }
);
